import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { RhythmPatternPlayer } from "@/components/mt/RhythmPatternPlayer";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory33() {
  const { lesson, course, accent } = findLesson("music-theory", "33");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT33-Time-Signatures-Animated.mp4"
        caption="Time signatures — what the two numbers really mean"
        hint="≈ 26 sec · watch first"
      />
      <Chapter number="33" eyebrow="Music Theory · Chapter Thirty-Three" title="The numerator and denominator of a time signature">
        <p>
          A time signature looks like a fraction — 4/4, 3/4, 6/8, 12/8 — and that visual
          similarity is responsible for almost every misunderstanding people have about
          meter. Because <em>they are not fractions</em>. They look like fractions. They
          read like fractions. They are not.
        </p>
        <p>
          A time signature is a pair of independent instructions stacked on top of each
          other. The top number tells you <strong>how many beats are in a bar</strong>.
          The bottom number tells you <strong>what kind of note counts as one beat</strong>.
          They aren't divided into each other — they answer two different questions.
          Once that lands, every meter on the planet becomes readable.
        </p>
        <MTQuote>
          The first thing to break to you is the numerator and the denominator have nothing
          to do with each other. So you may even want to stop saying 4 by 4 and instead say
          4 4, because 4 by 4 implies or tries to suggest that the two numbers have
          something to do with each other. They absolutely don't.
        </MTQuote>
        <NoteImage
          src="/notes/music-theory/finals/33a-4-4-time-signature-explanation.png"
          caption="Jason's note: 4/4 = 4 × 1/4 = four quarter-notes per bar. The time signature is NOT a fraction; it's two stacked instructions. The top says 'how many beats per bar' (the numerator). The bottom says 'what kind of note counts as one beat' (the denominator)."
        />
      </Chapter>

      <Chapter eyebrow="The numerator" title="How many beats per bar">
        <Prose>
          <p>
            Read the top number and ask the simplest possible question: <em>how many
            things am I counting before the bar restarts?</em> That's it. In 4/4, you
            count to four and start over. In 3/4, you count to three. In 7/8, you count
            to seven. The number on top is the count.
          </p>
          <p>
            That count also dictates where the <strong>downbeats</strong> sit and
            therefore the natural feel of the music. A 3 on top almost always means
            waltz-like motion (one-two-three, one-two-three). A 4 on top usually means
            the steady backbeat we associate with pop and rock. A 6 or 12 on top tends
            to group into two or four big beats, each with three subdivisions —
            jig-like, lilting.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The denominator" title="What kind of note is one beat">
        <Prose>
          <p>
            Now the bottom number. It's never a count — it's a <em>label</em>. The
            denominator is shorthand for which note value gets to be called "one beat":
          </p>
        </Prose>

        <TheoryBox label="Reading the denominator">
          <div className="space-y-1 font-mono text-[14px] text-amber-200">
            <div><strong>2</strong> &nbsp;→&nbsp; the half note (minim) is one beat</div>
            <div><strong>4</strong> &nbsp;→&nbsp; the quarter note (crotchet) is one beat</div>
            <div><strong>8</strong> &nbsp;→&nbsp; the eighth note (quaver) is one beat</div>
            <div><strong>16</strong> &nbsp;→&nbsp; the sixteenth note (semi-quaver) is one beat</div>
          </div>
          <div className="mt-2 text-[12px] text-nsm-dim">
            Same notes, different label. A bar of 4/4 and a bar of 4/8 contain the same
            number of beats — but a player will think of them differently because the
            denominator changes the visual notation and the suggested feel.
          </div>
        </TheoryBox>

        <Prose>
          <p>
            So <strong>4/4</strong> reads "four crotchet beats per bar." <strong>3/4</strong>
            reads "three crotchet beats per bar." <strong>6/8</strong> reads "six quaver
            beats per bar" — but in practice, that 6/8 nearly always groups into
            <em> two</em> dotted-quarter pulses (one-two-three / four-five-six), and that
            is the difference between simple and compound meter we'll explore in the
            next chapter.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The fraction trap" title="Why 6/8 is not 3/4">
        <Prose>
          <p>
            Here's the proof that time signatures are not fractions: <strong>6/8</strong>
            and <strong>3/4</strong> reduce to the same fraction (3/4 = 6/8 mathematically),
            yet they sound nothing alike. 3/4 is a waltz — three steady downbeats per
            bar. 6/8 is a jig — two compound beats, each subdivided into three. Same
            number of total eighth notes, totally different rhythmic feel.
          </p>
          <p>
            That's because the bottom number changes how a musician <em>groups</em> the
            count, not just how long each note is. A bar of 6/8 has six eighth notes
            grouped <strong>3+3</strong>; a bar of 3/4 has six eighth notes grouped
            <strong> 2+2+2</strong>. The grouping is what makes the music move, and the
            time signature encodes the grouping in a way no fraction ever could.
          </p>
        </Prose>

        <TheoryBox label="Same notes, different grouping">
          <div className="space-y-1 text-[14px]">
            <div><strong>3/4:</strong> <span className="font-mono">[1 &amp; 2 &amp; 3 &amp;]</span> — three quarter beats, each split in two</div>
            <div><strong>6/8:</strong> <span className="font-mono">[1-2-3 &nbsp; 4-5-6]</span> — two dotted-quarter beats, each split in three</div>
          </div>
          <div className="mt-2 text-[12px] text-nsm-dim">
            Six eighth notes either way. Two completely different feels.
          </div>
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="The role of the body" title="What the meter does to your head">
        <Prose>
          <p>
            The most reliable way to confirm a meter is in your <strong>body</strong>,
            not on the page. Listen to a piece of music and let your head move with the
            pulse. Whatever you naturally feel as the strong beat — the one your head
            nods to — is the downbeat. Count from there until your head nods again, and
            you have the numerator. That count, in any honest piece of music, is what
            the time signature is committed to.
          </p>
          <p>
            This bodily test is also how you tell duple meter (2 or 4 on top) from
            triple meter (3 on top) from compound meter (6, 9, 12). The body gives a
            different shape to each one — a duple bar has the feel of a forward march,
            triple has a swing or sway, compound has a galloping lilt. The same piece
            played in 4/4 and 6/8 would feel, in your spine, like two completely
            different songs.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Common meters" title="The signatures you'll see most">
        <TheoryBox label="The everyday meters">
          <div className="space-y-1 font-mono text-[14px] text-amber-200">
            <div><strong>2/4</strong> &nbsp;→&nbsp; march, polka. Short bars, strong-weak pairs.</div>
            <div><strong>3/4</strong> &nbsp;→&nbsp; waltz. Three beats per bar, downbeat heavy.</div>
            <div><strong>4/4</strong> &nbsp;→&nbsp; "common time." The default of pop, rock, country, R&amp;B, hip-hop.</div>
            <div><strong>6/8</strong> &nbsp;→&nbsp; compound duple. Two big beats subdivided in three. Jigs, ballads, gospel.</div>
            <div><strong>9/8</strong> &nbsp;→&nbsp; compound triple. Three big beats in three. Used sparingly — Celtic, modal jazz.</div>
            <div><strong>12/8</strong> &nbsp;→&nbsp; compound quadruple. Four big beats in three. Slow blues, doo-wop, gospel ballads.</div>
            <div><strong>5/4, 7/8</strong> &nbsp;→&nbsp; odd meters. Brubeck's "Take Five," prog rock, Indian compositions.</div>
          </div>
        </TheoryBox>

        <Prose>
          <p>
            Notice that every meter has a character — and that character isn't the
            mathematical reduction of the fraction; it's the grouping of pulses inside
            the bar. The next chapter takes this further: how each <em>beat</em> gets
            divided into 2, 3, or 4 sub-beats, and why that division reshapes the feel
            without ever changing the time signature.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Hear it" title="Steady pulses, varied groupings">
        <Prose>
          <p>
            The pattern player below gives you a feel for what a steady set of downbeats
            sounds like at common tempos. Listen for the strong-beat stroke at the
            beginning of each bar and the way grouping creates the meter without anyone
            actually saying so.
          </p>
        </Prose>

        <RhythmPatternPlayer
          patterns={[
            {
              name: "4/4 · simple",
              description: "Four kicks per bar. The most basic 4/4 pulse. Strong-weak-strong-weak.",
              cells: ["K","","","","K","","","","K","","","","K","","",""],
              bpm: 100,
            },
            {
              name: "3/4 · waltz",
              description: "Three beats per bar. Kick on 1, snare lifts on 2 and 3.",
              beats: 3,
              cells: ["K","","","","S","","","","S","","",""],
              bpm: 120,
            },
            {
              name: "6/8 · compound duple",
              description: "Six eighths per bar, grouped 3+3. Two strong pulses with three sub-beats each.",
              beats: 2,
              subdivide: 3,
              cells: ["K","","","S","",""],
              bpm: 80,
            },
          ]}
        />
      </Chapter>

      <Chapter eyebrow="Vocabulary" title="The terms to keep">
        <Glossary>
          <Term name="Numerator" def="The top number of a time signature. Counts the beats per bar." />
          <Term name="Denominator" def="The bottom number. Names the note value that counts as one beat (2 = half, 4 = quarter, 8 = eighth)." />
          <Term name="Bar (measure)" def="The unit of music between two bar-lines. The numerator tells you how many beats fit inside." />
          <Term name="Downbeat" def="The strong beat — usually beat 1 of a bar, sometimes also beat 3 in 4/4." />
          <Term name="Simple meter" def="Each beat divides into 2 sub-beats. 2/4, 3/4, 4/4 are simple meters." />
          <Term name="Compound meter" def="Each beat divides into 3 sub-beats. 6/8, 9/8, 12/8 are compound meters." />
        </Glossary>

        <TheoryBox label="One sentence to remember">
          The top number tells you <strong>how many</strong>. The bottom number tells you
          <strong> what kind</strong>. That's the whole signature.
        </TheoryBox>
        <NoteImage
          src="/notes/music-theory/finals/33c-7-8-and-3-2-examples.png"
          caption="Beyond 4/4: 8/4 (eight quarter-note beats per bar — a long bar). 7/8 (seven eighth-note beats per bar — usually grouped 4+3 or 3+4 for asymmetric Balkan/prog rhythms). 3/2 (three half-note beats per bar — slow waltzes, Renaissance music)."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-34"
        label="Chapter 34 · Beats & their Divisions"
        why="Now zoom inside one beat. We'll divide it by 2, by 3, by 4 — meet quavers, triplets, semi-quavers — and discover where swing comes from at the millisecond level."
      />
    </LessonShell>
  );
}
